import { useCallback } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { FileText, Link2, Upload, Database, Lightbulb, ClipboardList, X } from "lucide-react";
import { useDropzone } from "react-dropzone";
import { useDocHubTags } from "@/hooks/useDocHubTags";
import { useDocHubSnippetsTags } from "@/hooks/useDocHubSnippetsTags";
import docxIcon from "@/assets/file-icons/docx.svg";
import pdfIcon from "@/assets/file-icons/pdf.svg";
import pptxIcon from "@/assets/file-icons/pptx.svg";

export type SourceType = 'url' | 'text' | 'file' | 'dochub' | 'gdrive';

export interface SelectedDocHubItems {
  tips: string[];
  files: string[];
  moms: string[];
}

interface Tip {
  id: string;
  name: string;
  content: string;
  tags?: string[];
}

interface DocHubFile {
  id: string;
  name: string;
  file_type: string;
  link?: string;
  tags?: string[];
}

interface Mom {
  id: string;
  filename: string;
  summary?: string;
  transcript_content: string;
  status: string;
  created_at: string;
}

interface Brand {
  id: string;
  name: string;
}

function parseAcceptFromString(acceptStr: string): Record<string, string[]> {
  const extToMime: Record<string, string> = {
    ".pdf": "application/pdf",
    ".doc": "application/msword",
    ".docx": "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ".txt": "text/plain",
    ".md": "text/plain",
    ".pptx": "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    ".ppt": "application/vnd.ms-powerpoint",
  };
  const accept: Record<string, string[]> = {};
  acceptStr.split(",").forEach((part) => {
    const ext = part.trim().toLowerCase();
    if (ext.startsWith(".") && extToMime[ext]) {
      const mime = extToMime[ext];
      if (!accept[mime]) accept[mime] = [];
      accept[mime].push(ext);
    }
  });
  return Object.keys(accept).length > 0 ? accept : { "application/pdf": [".pdf"], "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"], "text/plain": [".txt"] };
}

interface FileDropZoneProps {
  fileInput: globalThis.File | null;
  onFileInputChange: (file: globalThis.File | null) => void;
  fileAccept?: string;
  fileHelpText?: string;
}

function FileDropZone({ fileInput, onFileInputChange, fileAccept = ".pdf,.doc,.docx,.txt", fileHelpText }: FileDropZoneProps) {
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      onFileInputChange(acceptedFiles[0] || null);
    },
    [onFileInputChange]
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept: parseAcceptFromString(fileAccept),
    maxFiles: 1,
    noClick: false,
    noKeyboard: false,
  });

  return (
    <div className="space-y-2">
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
          isDragActive ? "border-primary bg-primary/5" : "border-muted-foreground/25 hover:border-primary/50"
        }`}
      >
        <input {...getInputProps()} />
        {/* Stacked file type icons (docx, pdf, pptx) like documentation-hub/documents */}
        <div className="flex items-center justify-center gap-1 mb-4">
          <img src={docxIcon} alt="" className="h-10 w-10 object-contain -rotate-6" />
          <img src={pdfIcon} alt="" className="h-10 w-10 object-contain rotate-0 relative z-10" />
          <img src={pptxIcon} alt="" className="h-10 w-10 object-contain rotate-6" />
        </div>
        {fileInput ? (
          <div className="flex flex-col items-center gap-2">
            <p className="text-sm font-medium truncate max-w-full px-2">{fileInput.name}</p>
            <p className="text-xs text-muted-foreground">{(fileInput.size / 1024).toFixed(1)} KB</p>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onFileInputChange(null);
              }}
            >
              <X className="h-4 w-4 mr-1" />
              Remove file
            </Button>
          </div>
        ) : isDragActive ? (
          <p className="text-sm">Drop the file here...</p>
        ) : (
          <>
            <p className="text-sm font-medium mb-1">Drag and drop notes, readings, lecture slides, etc.</p>
            {fileHelpText && <p className="text-xs text-muted-foreground mb-3">{fileHelpText}</p>}
            <Button type="button" variant="secondary" size="sm" onClick={(e) => { e.stopPropagation(); open(); }}>
              Browse files
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

interface ContentSourceSelectorProps {
  sourceType: SourceType;
  onSourceTypeChange: (type: SourceType) => void;
  
  // Text input
  textInput: string;
  onTextInputChange: (value: string) => void;
  textPlaceholder?: string;
  textLabel?: string;
  
  // URL input
  urlInput: string;
  onUrlInputChange: (value: string) => void;
  urlPlaceholder?: string;
  urlLabel?: string;
  urlHelpText?: string;
  
  // File input (browser File object)
  fileInput: globalThis.File | null;
  onFileInputChange: (file: globalThis.File | null) => void;
  fileAccept?: string;
  fileLabel?: string;
  fileHelpText?: string;
  
  // Doc Hub
  selectedDocHubItems?: SelectedDocHubItems;
  onToggleTip?: (tipId: string) => void;
  onToggleFile?: (fileId: string) => void;
  onToggleMom?: (momId: string) => void;
  currentBrand?: Brand | null;
  tips?: Tip[];
  files?: DocHubFile[];
  moms?: Mom[];
  brandId?: string;
  
  // Google Drive (optional)
  showGoogleDrive?: boolean;
  onGoogleDriveSelect?: () => void;

  // Customization
  showDocHub?: boolean;
  className?: string;
}

export function ContentSourceSelector({
  sourceType,
  onSourceTypeChange,
  textInput,
  onTextInputChange,
  textPlaceholder = "Paste your content here...",
  textLabel = "Content",
  urlInput,
  onUrlInputChange,
  urlPlaceholder = "https://example.com/article",
  urlLabel = "Website URL",
  urlHelpText = "We'll extract content from this URL",
  fileInput,
  onFileInputChange,
  fileAccept = ".pdf,.doc,.docx,.txt",
  fileLabel = "Upload File",
  fileHelpText = "Supported formats: PDF, DOC, DOCX, TXT",
  selectedDocHubItems,
  onToggleTip,
  onToggleFile,
  onToggleMom,
  currentBrand,
  tips,
  files,
  moms,
  brandId,
  showGoogleDrive = false,
  onGoogleDriveSelect,
  showDocHub = true,
  className,
}: ContentSourceSelectorProps) {
  const { tags: fileTags } = useDocHubTags(brandId || '');
  const { tags: snippetTags } = useDocHubSnippetsTags(brandId || '');

  const getTagName = (tagId: string, isSnippet: boolean = false): string | null => {
    const tagList = isSnippet ? snippetTags : fileTags;
    const tag = tagList.find(t => t.id === tagId);
    return tag?.name || null;
  };

  const parseTags = (tags: any): string[] => {
    if (!tags) return [];
    if (Array.isArray(tags)) return tags.map(String);
    if (typeof tags === 'string') {
      try {
        const parsed = JSON.parse(tags);
        return Array.isArray(parsed) ? parsed.map(String) : [];
      } catch {
        return [];
      }
    }
    return [];
  };
  const tabCols = showGoogleDrive ? (showDocHub ? 5 : 4) : (showDocHub ? 4 : 3);

  return (
    <Tabs value={sourceType} onValueChange={(v) => onSourceTypeChange(v as SourceType)} className={className}>
      <TabsList className={`grid w-full ${tabCols === 5 ? 'grid-cols-5' : tabCols === 4 ? 'grid-cols-4' : 'grid-cols-3'}`}>
        <TabsTrigger value="text">
          <FileText className="h-4 w-4 mr-2" />
          Text
        </TabsTrigger>
        <TabsTrigger value="url">
          <Link2 className="h-4 w-4 mr-2" />
          URL
        </TabsTrigger>
        <TabsTrigger value="file">
          <Upload className="h-4 w-4 mr-2" />
          File
        </TabsTrigger>
        {showDocHub && (
          <TabsTrigger value="dochub">
            <Database className="h-4 w-4 mr-2" />
            Doc Hub
          </TabsTrigger>
        )}
        {showGoogleDrive && (
          <TabsTrigger value="gdrive">
            <img src="https://www.gstatic.com/images/branding/product/2x/drive_2020q4_48dp.png" alt="" className="h-4 w-4 mr-2" />
            Google Drive
          </TabsTrigger>
        )}
      </TabsList>

      <TabsContent value="text" className="space-y-4">
        <div>
          <Label htmlFor="text-input">{textLabel}</Label>
          <Textarea
            id="text-input"
            placeholder={textPlaceholder}
            className="min-h-[200px]"
            value={textInput}
            onChange={(e) => onTextInputChange(e.target.value)}
          />
        </div>
      </TabsContent>

      <TabsContent value="url" className="space-y-4">
        <div>
          <Label htmlFor="url-input">{urlLabel}</Label>
          <Input
            id="url-input"
            type="url"
            placeholder={urlPlaceholder}
            value={urlInput}
            onChange={(e) => onUrlInputChange(e.target.value)}
          />
          {urlHelpText && (
            <p className="text-sm text-muted-foreground mt-2">{urlHelpText}</p>
          )}
        </div>
      </TabsContent>

      <TabsContent value="file" className="space-y-4">
        <FileDropZone
          fileInput={fileInput}
          onFileInputChange={onFileInputChange}
          fileAccept={fileAccept}
          fileHelpText={fileHelpText}
        />
      </TabsContent>

      {showGoogleDrive && (
        <TabsContent value="gdrive" className="space-y-4">
          <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-8 text-center bg-muted/20">
            <img
              src="https://www.gstatic.com/images/branding/product/2x/drive_2020q4_48dp.png"
              alt="Google Drive"
              className="h-12 w-12 mx-auto mb-4"
            />
            <h3 className="font-semibold text-foreground mb-1">
              Select a Google Doc
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Uses the same Google account as your Calendar. Only Google Docs are supported for text import.
            </p>
            <Button
              type="button"
              variant="secondary"
              onClick={() => onGoogleDriveSelect?.()}
            >
              Select file
            </Button>
          </div>
        </TabsContent>
      )}

      {showDocHub && (
        <TabsContent value="dochub" className="space-y-2">
          {!currentBrand ? (
            <Alert>
              <AlertDescription>Please select a brand to view Doc Hub content</AlertDescription>
            </Alert>
          ) : (
            <ScrollArea className="h-[280px] border rounded-md p-2">
              {(() => {
                // Combine all items into a single list with type info
                const allItems: Array<{
                  id: string;
                  type: 'tip' | 'file' | 'mom';
                  name: string;
                  subtitle: string;
                  tags?: string[];
                  badge?: string;
                  isSelected: boolean;
                  onToggle: () => void;
                }> = [];

                // Add tips
                if (tips && onToggleTip && selectedDocHubItems) {
                  tips.forEach(tip => {
                    allItems.push({
                      id: tip.id,
                      type: 'tip',
                      name: tip.name,
                      subtitle: tip.content.substring(0, 50) + '...',
                      tags: parseTags(tip.tags),
                      isSelected: selectedDocHubItems.tips.includes(tip.id),
                      onToggle: () => onToggleTip(tip.id),
                    });
                  });
                }

                // Add files
                if (files && onToggleFile && selectedDocHubItems) {
                  files.forEach(file => {
                    allItems.push({
                      id: file.id,
                      type: 'file',
                      name: file.name,
                      subtitle: file.file_type,
                      tags: parseTags(file.tags),
                      badge: file.file_type,
                      isSelected: selectedDocHubItems.files.includes(file.id),
                      onToggle: () => onToggleFile(file.id),
                    });
                  });
                }

                // Add MOMs
                if (moms && onToggleMom && selectedDocHubItems) {
                  moms.forEach(mom => {
                    allItems.push({
                      id: mom.id,
                      type: 'mom',
                      name: mom.filename,
                      subtitle: mom.summary?.substring(0, 50) || mom.transcript_content.substring(0, 50) + '...',
                      badge: mom.status,
                      isSelected: selectedDocHubItems.moms.includes(mom.id),
                      onToggle: () => onToggleMom(mom.id),
                    });
                  });
                }

                if (allItems.length === 0) {
                  return (
                    <p className="text-muted-foreground text-center py-8 text-sm">
                      No Doc Hub content available
                    </p>
                  );
                }

                const getTypeIcon = (type: 'tip' | 'file' | 'mom') => {
                  switch (type) {
                    case 'tip':
                      return <Lightbulb className="h-4 w-4 text-primary shrink-0" />;
                    case 'file':
                      return <ClipboardList className="h-4 w-4 text-primary shrink-0" />;
                    case 'mom':
                      return <FileText className="h-4 w-4 text-primary shrink-0" />;
                  }
                };

                return (
                  <div className="space-y-1">
                    {allItems.map(item => (
                      <div
                        key={`${item.type}-${item.id}`}
                        className="flex items-center gap-2 p-2 rounded-md hover:bg-accent cursor-pointer"
                        onClick={item.onToggle}
                      >
                        <Checkbox 
                          checked={item.isSelected}
                          onCheckedChange={item.onToggle}
                        />
                        {getTypeIcon(item.type)}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{item.name}</p>
                          <p className="text-xs text-muted-foreground truncate">{item.subtitle}</p>
                        </div>
                        {item.badge && (
                          <Badge variant="outline" className="text-[10px] px-1 py-0 shrink-0">
                            {item.badge}
                          </Badge>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })()}
            </ScrollArea>
          )}
        </TabsContent>
      )}
    </Tabs>
  );
}
